import React from 'react';
import { X, GripVertical } from 'lucide-react';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@rocco/components/select';
import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';
import { BuildHubRoleEnum } from 'generated/graphql';
import type { BuildHubCollaborator } from '@modules/build-hub/types';
import { getInitials } from '@modules/build-hub/utils/get-initials';
import { getCollaboratorAvatar } from '@modules/build-hub/utils/get-collaborator-avatar';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

interface CollaboratorRowProps {
    collaborator: BuildHubCollaborator;
    ownerId: number;
    onRoleChange?: (newRole: BuildHubRoleEnum) => void;
    onRemove?: (collaboratorId: string) => void;
    onReInvite?: (collaboratorId: string) => void;
    enableSorting?: boolean;
    /** Available roles that can be assigned. If not provided, all roles are shown. */
    availableRoles?: BuildHubRoleEnum[];
}

export const CollaboratorRow = ({
    collaborator,
    ownerId,
    onRoleChange,
    onRemove,
    onReInvite,
    enableSorting = false,
    availableRoles,
}: CollaboratorRowProps) => {
    const { id, email, name, role, lastInviteLink, onBoarded, memberID } =
        collaborator;

    const isOwner = String(ownerId) === memberID;

    // Use provided availableRoles or fall back to all roles
    const rolesToDisplay = availableRoles ?? Object.values(BuildHubRoleEnum);

    // Use the sortable hook only when sorting is enabled
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({
        id: id,
        disabled: !enableSorting,
    });

    const style = enableSorting
        ? {
              transform: CSS.Transform.toString(transform),
              transition,
              opacity: isDragging ? 0.5 : 1,
          }
        : {};

    return (
        <div
            ref={enableSorting ? setNodeRef : undefined}
            style={style}
            className="flex items-center justify-between"
            {...(enableSorting ? attributes : {})}
        >
            <div className="flex items-center gap-3">
                {/* Drag Handle - only show when sorting is enabled */}
                {enableSorting && (
                    <div
                        {...listeners}
                        className="cursor-grab text-gray-400 hover:text-gray-600 active:cursor-grabbing"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <GripVertical className="h-4 w-4" />
                    </div>
                )}
                <Avatar size="square40">
                    <AvatarImage
                        src={getCollaboratorAvatar(collaborator)}
                        alt={name}
                        fit="cover"
                    />
                    <AvatarFallback size="md">
                        {getInitials(onBoarded ? name : email)}
                    </AvatarFallback>
                </Avatar>
                <div>
                    <div className="flex items-center gap-2">
                        <p className="text-sm font-medium">
                            {onBoarded ? name : email}
                        </p>
                        {isOwner && (
                            <span className="inline-flex items-center rounded-full bg-black px-2 py-0.5 text-xs font-medium text-white">
                                Project Owner
                            </span>
                        )}
                    </div>
                    {onBoarded ? (
                        <p className="text-xs text-gray-500">{email}</p>
                    ) : (
                        <p className="max-w-[200px] truncate text-xs text-gray-500">
                            DEBUG: {lastInviteLink}
                        </p>
                    )}
                </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
                {onReInvite && (
                    <button
                        onClick={() => onReInvite(id)}
                        className="whitespace-nowrap px-2 text-gray-500 transition-opacity hover:text-red-600"
                        type="button"
                        title="Re-invite"
                    >
                        <span>Re-invite</span>
                    </button>
                )}
                {onRoleChange ? (
                    isOwner ? (
                        <span className="px-4 py-2 capitalize text-gray-500">
                            {role}
                        </span>
                    ) : (
                        <Select value={role} onValueChange={onRoleChange}>
                            <SelectTrigger
                                color="transparent"
                                className="w-full text-sm"
                            >
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {rolesToDisplay.map((value) => (
                                    <SelectItem key={value} value={value}>
                                        {value}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    )
                ) : (
                    <span className="capitalize text-gray-500">{role}</span>
                )}
                {onRemove && (
                    <button
                        onClick={() => onRemove(id)}
                        className="text-gray-500 transition-opacity hover:text-red-600"
                        type="button"
                        title="Remove"
                    >
                        <X className="h-4 w-4" />
                        <span className="sr-only">Remove collaborator</span>
                    </button>
                )}
            </div>
        </div>
    );
};
